import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { Form, Formik } from 'formik';
import update from 'immutability-helper';
import { isEmpty, isEqual, uniqueId } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import * as Yup from 'yup';

import Button from '@dashboard/components/Button';
import FileIcon from '@dashboard/components/FileIcon';
import { FormElement, Label } from '@dashboard/components/Form';
import FormSubmit from '@dashboard/components/Form/components/FormSubmit';
import { TextInput } from '@dashboard/components/Formik';
import FormError, { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useFileQueries from '@dashboard/hooks/queries/useFileQueries';
import { confirm } from '@dashboard/lib/confirm';
import downloadUrlAsFile from '@dashboard/services/downloadUrlAsFile';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import logException from '@dashboard/services/logException';
import useProduct from '../useProduct';
import clsx from 'clsx';
import usePage from '@dashboard/hooks/usePage';
import getFileTypeLabel from '../getFileTypeLabel';
import getFileSizeLabel from '../getFileSizeLabel';
import Lozenge from '@dashboard/components/Lozenge';
import Tooltip from '@/dashboard/components/Tooltip';

function filesReducer(state, action) {
  switch (action.type) {
    case 'set': {
      return update(state, {
        $set: action.values,
      });
    }
    case 'add':
      if (state && state.length > 0) {
        return update(state, { $push: action.values });
      } else {
        return action.values;
      }
    case 'update': {
      const index = action.file._id
        ? state.findIndex(file => file._id === action.file._id)
        : state.findIndex(file => file.key === action.file.key);

      if (index === -1) return state;
      return update(state, {
        [index]: {
          $set: action.values,
        },
      });
    }
    case 'delete': {
      if (action.file._id) {
        return state.filter(f => f._id !== action.file._id);
      } else {
        return state.filter(f => f.key !== action.file.key);
      }
    }
    default:
      return state;
  }
}

const ProductFilesForm = () => {
  const { refetch: refetchPage } = usePage();
  const { product, updateProduct } = useProduct();
  const [files, dispatchFiles] = useReducer(filesReducer, product.files || []);
  const { uploadFile, deleteFile } = useFileQueries();
  const { sellerId } = useAuth();

  useEffect(() => {
    async function addFilesToProduct(fileIds) {
      const { error } = await updateProduct.mutateAsync({ files: fileIds });

      if (error) throw error;
    }

    const fileIds = files?.map(file => file._id).filter(Boolean);
    const productFileIds = product.files?.map(file => file._id);

    if (!isEqual(fileIds, productFileIds)) {
      addFilesToProduct(fileIds);
    }
  }, [files]);

  const onDrop = useCallback(
    async acceptedFiles => {
      const acceptedFilesToSet = acceptedFiles.map(file =>
        Object.assign(file, {
          key: uniqueId(),
          preview: URL.createObjectURL(file),
          isUploading: true,
          isUploaded: false,
        }),
      );

      dispatchFiles({ type: 'add', values: acceptedFilesToSet });

      async function upload(file) {
        try {
          const { data, error } = await uploadFile({
            file,
            bucket: 'files',
            sellerId,
            onProgress: progress => {
              dispatchFiles({
                type: 'update',
                file,
                // We cannot effectively spread the file object as some properties are getters and so are removed by the spread.
                values: {
                  ...file,
                  type: file.type,
                  name: file.name,
                  size: file.size,
                  uploadProgress: progress,
                },
              });
            },
          });

          if (error) {
            throw error;
          }

          refetchPage();

          dispatchFiles({
            type: 'update',
            file,
            values: { ...data, uploadProgress: 100 },
          });
        } catch (error) {
          logException({ message: 'Error in uploading download', error });
          dispatchFiles({
            type: 'delete',
            file,
          });
        }
      }

      await Promise.all(acceptedFilesToSet.map(upload));
    },
    [files],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  async function onEditFile(file, values) {
    dispatchFiles({ type: 'update', file, values });

    refetchPage();
  }

  async function onDeleteFile(file) {
    if (file._id) {
      await deleteFile(file._id);

      await updateProduct.mutateAsync({
        files: files?.filter(f => f._id !== file._id)?.map(f => f._id),
        variants: product.variants?.map(v => ({
          ...v,
          options: v.options?.map(o => ({
            ...o,
            files: o.files?.filter(f => f !== file._id),
          })),
        })),
      });
    }

    refetchPage();

    dispatchFiles({ type: 'delete', file });
  }

  return (
    <div>
      <Files
        productId={product._id}
        files={files}
        onDeleteFile={onDeleteFile}
        onEditFile={onEditFile}
      />

      <div
        {...getRootProps()}
        className="transition-all p-3 border border-gray-300 rounded-md cursor-pointer focus:outline-none focus:border-chocolate-600 focus:border-solid hover hover:border-chocolate-600 hover:border-solid border-dashed"
      >
        <input {...getInputProps()} />

        <div className="flex justify-start items-center">
          <div className="mr-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1}
              stroke="currentColor"
              className="w-10 h-10 text-gray-400"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m3.75 9v6m3-3H9m1.5-12H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
              />
            </svg>
          </div>

          <div>
            <div className="flex text-gray-700">
              <label
                htmlFor="file-upload"
                className="relative cursor-pointer bg-white rounded-md font-medium text-chocolate-600 hover:text-chocolate-600 focus-within:outline-none"
              >
                <span>Upload files</span>
              </label>
              <p className="pl-1">or drag and drop</p>
            </div>
          </div>
        </div>

        <FileContainer isDragActive={isDragActive} files={files} productId={product._id} />
      </div>
    </div>
  );
};

function Files({ files, productId, onDeleteFile, onEditFile }) {
  if (!files || !files.length > 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-x-3 overflow-y-auto w-full border border-gray-200 rounded mb-4">
      {files.map((file, index) => (
        <File
          file={file}
          productId={productId}
          onDeleteFile={onDeleteFile}
          onEditFile={onEditFile}
          isLast={index + 1 === files.length}
          key={index}
        />
      ))}
    </div>
  );
}

Files.propTypes = {
  files: PropTypes.array,
  productId: PropTypes.string.isRequired,
  onDeleteFile: PropTypes.func.isRequired,
  onEditFile: PropTypes.func.isRequired,
};

function File({ file, onDeleteFile, onEditFile, productId, isLast }) {
  const { product } = useProduct();
  const [isEditing, setIsEditing] = useState(false);
  const { downloadFile } = useFileQueries();
  const [showProgress, setShowProgress] = useState(file.isUploading);

  useEffect(() => {
    if (file.isUploading) {
      setShowProgress(true);
    } else {
      const timer = setTimeout(() => setShowProgress(false), 500);
      return () => clearTimeout(timer);
    }
  }, [file.isUploading]);

  async function onDelete() {
    const del = await confirm('Delete this file?', {
      confirm: 'Delete',
      description: `Past customers won't be able to download this file anymore.`,
    });

    if (del) {
      try {
        await onDeleteFile(file);
      } catch (error) {
        logException(error);
        window.alert('Failed to delete file. Please try again or contact support');
      }
    }
  }

  async function onDownload() {
    const response = await downloadFile(file._id);

    if (response?.data?.url) {
      downloadUrlAsFile(response.data.url, file.name);
    } else {
      window.alert('Unable to download file. Try again or contact support.');
    }
  }

  const isAttachedToVariantOption = product.variants.some(variant =>
    variant.options.some(option => option.files?.includes(file._id)),
  );

  return (
    <div>
      <div
        className={clsx('p-4', {
          'border-gray-200 border-b': !isLast,
        })}
      >
        <div className={`flex flex-row gap-2 items-center justify-start`}>
          <div className="p-1">
            <FileIcon type={file.type} />
          </div>

          <div className="truncate">
            <div className="font-sm truncate">{file.name}</div>
            {getFileTypeLabel(file.type) ? (
              <div className="text-xs">
                {getFileTypeLabel(file.type)} • {getFileSizeLabel(file.size)}
              </div>
            ) : (
              <div className="text-xs">{getFileSizeLabel(file.size)}</div>
            )}
          </div>

          <div className="flex ml-auto gap-2 items-center">
            <FileActions
              file={file}
              onEdit={() => setIsEditing(!isEditing)}
              onDelete={onDelete}
              onDownload={onDownload}
            />
          </div>
        </div>

        {isAttachedToVariantOption && (
          <div className="ml-8">
            <Lozenge>Attached to variant(s)</Lozenge>
          </div>
        )}
      </div>

      {isEditing && (
        <div className={`p-4 ${!isLast && 'border-b border-gray-200'}`}>
          <FileEditForm
            file={file}
            productId={productId}
            onEditEnd={values => {
              onEditFile(file, values);
              setIsEditing(false);
            }}
          />
        </div>
      )}

      {showProgress && (
        <div className="relative w-full h-1 bg-gray-200 rounded">
          <div
            className="absolute h-full bg-chocolate-1000 rounded transition-all duration-300"
            style={{
              width: `${file.uploadProgress ?? 1}%`,
            }}
          />
        </div>
      )}
    </div>
  );
}

File.propTypes = {
  file: PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.string,
    isUploading: PropTypes.bool,
    isUploaded: PropTypes.bool,
    _id: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  onDeleteFile: PropTypes.func.isRequired,
  onEditFile: PropTypes.func.isRequired,
  productId: PropTypes.string.isRequired,
};

function FileEditForm({ file, onEditEnd }) {
  const { updateFile } = useFileQueries();

  return (
    <div>
      <Formik
        initialValues={{ name: file.name }}
        validationSchema={Yup.object().shape({
          name: Yup.string().required('Name is required'),
        })}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={async (values, actions) => {
          actions.setStatus(undefined);

          try {
            const { data, error } = await updateFile(file._id, values);

            if (data) {
              actions.setStatus('');
              actions.resetForm({ values: { name: data.name } });
              onEditEnd(data);
            } else {
              const { status, errors } = transformApiErrorToFormik(error);

              if (!isEmpty(errors)) {
                actions.setErrors(errors);
              } else {
                actions.setStatus(status);
              }
            }
          } catch (error) {
            if (error && typeof error.message === 'string') {
              actions.setStatus(error.message);
            } else {
              actions.setStatus("Couldn't save form. Please contact support");
            }
          }
        }}
      >
        {({ dirty, status }) => (
          <Form>
            <FormElement>
              <Label htmlFor="name">Name</Label>
              <TextInput id="name" name="name" type="text" placeholder="Name" />
              <FormError name="name" />
            </FormElement>

            <FormErrorBase>{status && status}</FormErrorBase>

            <FormSubmit>
              <FormSubmit.Button>Save</FormSubmit.Button>
              <FormSubmit.State visible={!!dirty} />
            </FormSubmit>
          </Form>
        )}
      </Formik>
    </div>
  );
}

FileEditForm.propTypes = {
  file: PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.string,
    isUploading: PropTypes.bool,
    isUploaded: PropTypes.bool,
    _id: PropTypes.string,
  }).isRequired,
  productId: PropTypes.string.isRequired,
  onEditEnd: PropTypes.func.isRequired,
};

function FileActions({ onEdit, onDelete, onDownload, file }) {
  return (
    <div className="flex gap-2">
      <Tooltip label="Download">
        <Button appearance="subtle" onClick={onDownload} disabled={!file._id} aria-label="download">
          <ArrowDownTrayIcon height="16" />
        </Button>
      </Tooltip>
      <Tooltip label="Edit">
        <Button appearance="subtle" onClick={onEdit} disabled={!file._id} aria-label="edit">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            height="16px"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
            />
          </svg>
        </Button>
      </Tooltip>

      <Tooltip label="Delete">
        <Button appearance="subtle" onClick={onDelete} disabled={!file._id} aria-label="delete">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            height="16px"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </Button>
      </Tooltip>
    </div>
  );
}

FileActions.propTypes = {
  file: PropTypes.shape({
    _id: PropTypes.string,
  }),
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

function FileContainer({ isDragActive }) {
  if (isDragActive) {
    return <p></p>;
  }

  return <p></p>;
}

FileContainer.propTypes = {
  isDragActive: PropTypes.func.isRequired,
};

export default ProductFilesForm;
